import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  EmployeeReferenceType,
  MessageThread,
  MessageThreadType,
  getDefaultUserAvatar,
} from "winslow-core";
import {
  selectEmployees,
  selectPersonas,
  selectEmployeeRecords,
} from "../redux/features/agent/agentSlice";
import { erGetCPValue } from "../components/app/app-employee-records/employeeRecordUtils";

export type ThreadContextInfo = {
  avatar?: string;
  label: string;
  threadType: MessageThreadType;
  employeeId?: string;
  employeeReferenceType?: EmployeeReferenceType;
} | null;

export const useMessageThreadInfo = (
  messageThread?: MessageThread
): ThreadContextInfo => {
  const employees = useSelector(selectEmployees);
  const personas = useSelector(selectPersonas);
  const employeeRecords = useSelector(selectEmployeeRecords);
  const [contextInfo, setContextInfo] = useState<ThreadContextInfo>(null);

  useEffect(() => {
    if (!messageThread) {
      setContextInfo(null);
      return;
    }

    if (messageThread.threadType === MessageThreadType.NONE) {
      setContextInfo({
        label: "",
        threadType: MessageThreadType.NONE,
      });
      return;
    }

    if (messageThread.threadType === MessageThreadType.PERSONA) {
      const persona = personas.find((p) => p.id === messageThread.personaId);
      if (persona) {
        setContextInfo({
          avatar: "/icons/light-mode/persona_manager_off.svg",
          label: persona.name,
          threadType: MessageThreadType.PERSONA,
        });
        return;
      } else {
        setContextInfo({
          label: "This persona is no longer available.",
          threadType: MessageThreadType.PERSONA,
        });
        return;
      }
    } else if (messageThread.threadType === MessageThreadType.EMPLOYEE) {
      // Check if this is an EmployeeRecord (native HRIS) or Employee (external HRIS)
      if (
        messageThread.employeeReferenceType ===
          EmployeeReferenceType.EMPLOYEE_RECORD &&
        messageThread.employeeId
      ) {
        // Handle EmployeeRecord (native HRIS)
        const employeeRecord = employeeRecords?.find(
          (er) => er.id === messageThread.employeeId
        );
        if (employeeRecord) {
          const firstName = erGetCPValue(employeeRecord, "system-firstName");
          const lastName = erGetCPValue(employeeRecord, "system-lastName");
          const displayName =
            [firstName, lastName].filter(Boolean).join(" ").trim() ||
            employeeRecord.workEmail ||
            "Employee";

          setContextInfo({
            avatar:
              employeeRecord.avatarURL || getDefaultUserAvatar(displayName),
            label: displayName,
            threadType: MessageThreadType.EMPLOYEE,
            employeeId: employeeRecord.id,
            employeeReferenceType: EmployeeReferenceType.EMPLOYEE_RECORD,
          });
          return;
        } else {
          setContextInfo({
            label: "This employee is no longer available.",
            threadType: MessageThreadType.EMPLOYEE,
          });
          return;
        }
      } else if (messageThread.employeeId) {
        // Handle Employee (external HRIS)
        const employee = employees.find(
          (e) => e.id === messageThread.employeeId
        );
        if (employee) {
          setContextInfo({
            avatar:
              employee?.profile?.avatar ||
              getDefaultUserAvatar(employee.displayName),
            label: employee.displayName || employee.email,
            threadType: MessageThreadType.EMPLOYEE,
            employeeId: employee.id,
            employeeReferenceType: EmployeeReferenceType.EMPLOYEE,
          });
          return;
        } else {
          setContextInfo({
            label: "This employee is no longer available.",
            threadType: MessageThreadType.EMPLOYEE,
          });
          return;
        }
      }
    }

    setContextInfo(null);
  }, [messageThread, employees, personas, employeeRecords]);

  return contextInfo;
};
